/**
 * Two-Factor Authentication Settings Component
 * 
 * Provides UI for enabling/disabling 2FA with QR code and backup codes
 */

import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldAlt,
  faQrcode,
  faKey,
  faCheck,
  faTimes,
  faSpinner,
  faExclamationTriangle,
  faCopy,
  faDownload,
  faTrash,
  faRedo,
} from '@fortawesome/free-solid-svg-icons';
import { api } from '../services/api';
import { useI18n } from '../hooks/useI18n';
import { useTheme } from '../hooks/useTheme';
import '../styles/TwoFactorSettings.css';

interface TwoFactorStatus {
  enabled: boolean;
  backupCodesRemaining: number;
  lastVerified?: string;
}

interface SetupData {
  secret: string;
  qrCodeUrl: string;
  backupCodes: string[];
}

const TwoFactorSettings = () => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [setupMode, setSetupMode] = useState<'idle' | 'setup' | 'verify' | 'disable' | 'regenerate'>('idle');
  const [setupData, setSetupData] = useState<SetupData | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [newBackupCodes, setNewBackupCodes] = useState<string[] | null>(null);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const response = await api.get('/2fa/status');
      setStatus(response.data);
    } catch (err) {
      setError('Failed to load 2FA status');
    } finally {
      setLoading(false);
    }
  };

  const handleStartSetup = async () => {
    try {
      setProcessing(true);
      setError(null);
      const response = await api.post('/2fa/setup');
      setSetupData(response.data);
      setSetupMode('setup');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to start 2FA setup');
    } finally {
      setProcessing(false);
    }
  };

  const handleVerifySetup = async () => {
    if (verificationCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    try {
      setProcessing(true);
      setError(null);
      const response = await api.post('/2fa/confirm', { token: verificationCode });
      
      if (response.data.success) {
        setSuccess('Two-factor authentication enabled successfully!');
        setSetupMode('idle');
        setShowBackupCodes(true);
        await fetchStatus();
      } else {
        setError(response.data.message || 'Invalid verification code');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Verification failed');
    } finally {
      setProcessing(false);
      setVerificationCode('');
    }
  };

  const handleDisable2FA = async () => {
    if (verificationCode.length < 6) {
      setError('Please enter a verification code');
      return;
    }

    try {
      setProcessing(true);
      setError(null);
      const response = await api.post('/2fa/disable', { token: verificationCode });
      
      if (response.data.success) {
        setSuccess('Two-factor authentication disabled');
        setSetupMode('idle');
        setSetupData(null);
        await fetchStatus();
      } else {
        setError(response.data.message || 'Invalid verification code');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to disable 2FA');
    } finally {
      setProcessing(false);
      setVerificationCode('');
    }
  };

  const handleRegenerateBackupCodes = async () => {
    if (verificationCode.length < 6) {
      setError('Please enter a verification code');
      return;
    }

    try {
      setProcessing(true);
      setError(null);
      const response = await api.post('/2fa/backup-codes', { token: verificationCode });
      
      if (response.data.success) {
        setNewBackupCodes(response.data.backupCodes);
        setSuccess('New backup codes generated');
        setSetupMode('idle');
        await fetchStatus();
      } else {
        setError(response.data.message || 'Invalid verification code');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to regenerate backup codes');
    } finally {
      setProcessing(false);
      setVerificationCode('');
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCode(text);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch {
      setError('Failed to copy to clipboard');
    }
  };

  const downloadBackupCodes = (codes: string[]) => {
    const content = `Two-Factor Authentication Backup Codes
========================================

Keep these codes safe! Each code can only be used once.

${codes.map((code, i) => `${i + 1}. ${code}`).join('\n')}

Generated: ${new Date().toISOString()}
`;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = '2fa-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  if (loading) {
    return (
      <div className={`twofa-settings theme-${theme}`}>
        <div className="loading-state">
          <FontAwesomeIcon icon={faSpinner} spin />
          <span>{t('twofa.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`twofa-settings theme-${theme}`}>
      <div className="twofa-header">
        <div className="header-icon">
          <FontAwesomeIcon icon={faShieldAlt} />
        </div>
        <div className="header-text">
          <h3>{t('twofa.title')}</h3>
          <p>{t('twofa.subtitle')}</p>
        </div>
        <div className={`status-badge ${status?.enabled ? 'enabled' : 'disabled'}`}>
          <FontAwesomeIcon icon={status?.enabled ? faCheck : faTimes} />
          {status?.enabled ? t('twofa.enabled') : t('twofa.disabled')}
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="alert alert-error">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span>{error}</span>
          <button onClick={clearMessages}>&times;</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <FontAwesomeIcon icon={faCheck} />
          <span>{success}</span>
          <button onClick={clearMessages}>&times;</button>
        </div>
      )}

      {/* Main Content */}
      <div className="twofa-content">
        {/* Idle State - Not Enabled */}
        {setupMode === 'idle' && !status?.enabled && (
          <div className="setup-prompt">
            <div className="benefit-list">
              <h4>{t('twofa.whyEnable')}</h4>
              <ul>
                <li>{t('twofa.protectsAccount')}</li>
                <li>{t('twofa.timeBasedCodes')}</li>
                <li>{t('twofa.backupAccess')}</li>
              </ul>
            </div>
            <button 
              className="btn-primary setup-btn"
              onClick={handleStartSetup}
              disabled={processing}
            >
              {processing ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faQrcode} />}
              {t('twofa.enableTwoFactor')}
            </button>
          </div>
        )}

        {/* Idle State - Enabled */}
        {setupMode === 'idle' && status?.enabled && (
          <div className="enabled-state">
            <div className="status-info">
              <p className="protected">
                <FontAwesomeIcon icon={faCheck} /> {t('twofa.accountProtected')}
              </p>
              <p className="backup-info">
                <FontAwesomeIcon icon={faKey} />
                {t('twofa.backupCodesRemaining').replace('{count}', String(status.backupCodesRemaining))}
              </p>
            </div>
            
            <div className="action-buttons">
              <button 
                className="btn-secondary"
                onClick={() => setSetupMode('regenerate')}
              >
                <FontAwesomeIcon icon={faRedo} />
                {t('twofa.regenerateBackupCodes')}
              </button>
              <button 
                className="btn-danger"
                onClick={() => setSetupMode('disable')}
              >
                <FontAwesomeIcon icon={faTrash} />
                {t('twofa.disable')}
              </button>
            </div>
          </div>
        )}

        {/* Setup Mode */}
        {setupMode === 'setup' && setupData && (
          <div className="setup-flow">
            <div className="step">
              <h4>{t('twofa.scanQrCode')}</h4>
              <p>{t('twofa.scanQrDesc')}</p>
              <div className="qr-container">
                {/* Generate QR code using URL */}
                <img 
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(setupData.qrCodeUrl)}`}
                  alt="2FA QR Code"
                  className="qr-code"
                />
              </div>
              <div className="manual-entry">
                <p>{t('twofa.cantScan')}</p>
                <div className="secret-code">
                  <code>{setupData.secret}</code>
                  <button 
                    className="copy-btn"
                    onClick={() => copyToClipboard(setupData.secret)}
                    title="Copy secret"
                  >
                    <FontAwesomeIcon icon={copiedCode === setupData.secret ? faCheck : faCopy} />
                  </button>
                </div>
              </div>
            </div>

            <div className="step">
              <h4>{t('twofa.saveBackupCodes')}</h4>
              <p>{t('twofa.saveBackupDesc')}</p>
              <div className="backup-codes">
                {setupData.backupCodes.map((code, index) => (
                  <div key={index} className="backup-code">
                    <code>{code}</code>
                    <button 
                      className="copy-btn"
                      onClick={() => copyToClipboard(code)}
                    >
                      <FontAwesomeIcon icon={copiedCode === code ? faCheck : faCopy} />
                    </button>
                  </div>
                ))}
              </div>
              <button 
                className="btn-secondary download-btn"
                onClick={() => downloadBackupCodes(setupData.backupCodes)}
              >
                <FontAwesomeIcon icon={faDownload} />
                {t('twofa.downloadBackupCodes')}
              </button>
            </div>

            <div className="step">
              <h4>{t('twofa.verifyStep')}</h4>
              <p>{t('twofa.verifyDesc')}</p>
              <div className="verify-input">
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  autoComplete="one-time-code"
                />
                <button 
                  className="btn-primary"
                  onClick={handleVerifySetup}
                  disabled={processing || verificationCode.length !== 6}
                >
                  {processing ? <FontAwesomeIcon icon={faSpinner} spin /> : t('twofa.verifyAndEnable')}
                </button>
              </div>
            </div>

            <button 
              className="btn-text cancel-btn"
              onClick={() => {
                setSetupMode('idle');
                setSetupData(null);
                setVerificationCode('');
              }}
            >
              {t('twofa.cancelSetup')}
            </button>
          </div>
        )}

        {/* Disable Mode */}
        {setupMode === 'disable' && (
          <div className="disable-flow">
            <div className="warning-box">
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <div>
                <h4>{t('twofa.disableConfirm')}</h4>
                <p>{t('twofa.lessSecure')}</p>
              </div>
            </div>
            
            <div className="verify-section">
              <p>{t('twofa.enterCodeConfirm')}</p>
              <div className="verify-input">
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/[^0-9A-Za-z-]/g, '').slice(0, 9))}
                  placeholder={t('twofa.codeOrBackup')}
                  autoComplete="one-time-code"
                />
                <button 
                  className="btn-danger"
                  onClick={handleDisable2FA}
                  disabled={processing}
                >
                  {processing ? <FontAwesomeIcon icon={faSpinner} spin /> : t('twofa.disable')}
                </button>
              </div>
            </div>
            
            <button 
              className="btn-text"
              onClick={() => {
                setSetupMode('idle');
                setVerificationCode('');
              }}
            >
              Cancel
            </button>
          </div>
        )}

        {/* Regenerate Backup Codes Mode */}
        {setupMode === 'regenerate' && (
          <div className="regenerate-flow">
            <div className="info-box">
              <FontAwesomeIcon icon={faKey} />
              <div>
                <h4>{t('twofa.regenerateTitle')}</h4>
                <p>{t('twofa.regenerateWarning')}</p>
              </div>
            </div>
            
            <div className="verify-section">
              <p>{t('twofa.enterCodeConfirm')}</p>
              <div className="verify-input">
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  autoComplete="one-time-code"
                />
                <button 
                  className="btn-primary"
                  onClick={handleRegenerateBackupCodes}
                  disabled={processing || verificationCode.length !== 6}
                >
                  {processing ? <FontAwesomeIcon icon={faSpinner} spin /> : t('twofa.generateNew')}
                </button>
              </div>
            </div>
            
            <button 
              className="btn-text"
              onClick={() => {
                setSetupMode('idle');
                setVerificationCode('');
              }}
            >
              Cancel
            </button>
          </div>
        )}

        {/* Show New Backup Codes */}
        {newBackupCodes && (
          <div className="new-codes-modal">
            <div className="modal-content">
              <h4><FontAwesomeIcon icon={faKey} /> {t('twofa.newCodes')}</h4>
              <p>{t('twofa.saveNewCodes')}</p>
              <div className="backup-codes">
                {newBackupCodes.map((code, index) => (
                  <div key={index} className="backup-code">
                    <code>{code}</code>
                    <button 
                      className="copy-btn"
                      onClick={() => copyToClipboard(code)}
                    >
                      <FontAwesomeIcon icon={copiedCode === code ? faCheck : faCopy} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="modal-actions">
                <button 
                  className="btn-secondary"
                  onClick={() => downloadBackupCodes(newBackupCodes)}
                >
                  <FontAwesomeIcon icon={faDownload} /> {t('twofa.download')}
                </button>
                <button 
                  className="btn-primary"
                  onClick={() => setNewBackupCodes(null)}
                >
                  {t('twofa.done')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Show Backup Codes After Setup */}
        {showBackupCodes && setupData && (
          <div className="new-codes-modal">
            <div className="modal-content">
              <h4><FontAwesomeIcon icon={faCheck} /> {t('twofa.twofaEnabled')}</h4>
              <p>{t('twofa.saveBeforeClose')}</p>
              <div className="backup-codes">
                {setupData.backupCodes.map((code, index) => (
                  <div key={index} className="backup-code">
                    <code>{code}</code>
                    <button 
                      className="copy-btn"
                      onClick={() => copyToClipboard(code)}
                    >
                      <FontAwesomeIcon icon={copiedCode === code ? faCheck : faCopy} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="modal-actions">
                <button 
                  className="btn-secondary"
                  onClick={() => downloadBackupCodes(setupData.backupCodes)}
                >
                  <FontAwesomeIcon icon={faDownload} /> Download
                </button>
                <button 
                  className="btn-primary"
                  onClick={() => {
                    setShowBackupCodes(false);
                    setSetupData(null);
                  }}
                >
                  {t('twofa.savedMyCodes')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TwoFactorSettings;
